import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getFramework } from '@/framework';
import type { Cell, ITableLayer } from '@/version-types';
import { getTableEditState, type CellsSelectedInfo } from '../plugins/edit/state';
import addOrUpdateLayer from '../utils/add-or-update-layer';
import { applyTableLayout } from '../utils/table-layout';

export const mergeCells = ({
    frameworkIdentity,
    tableLayer,
    targetCellIds,
}: {
    frameworkIdentity: string;
    tableLayer: ITableLayer;
    targetCellIds: string[];
}) => {
    const framework = getFramework(frameworkIdentity);
    const { setState } = getTableEditState(frameworkIdentity);
    let newTableLayer = cloneDeep(tableLayer);
    const { cells: cellsProp } = newTableLayer.data;
    const cells = cellsProp.flat().filter(Boolean) as Cell[];
    const targetCells = cells.filter((cell) => targetCellIds.includes(cell.__renderId));
    // 最左上角的cell, 其它cell都合并到这个里面
    const targetCell = (() => {
        const sortedTargetCells = targetCells.sort((a, b) => (a.hIndex <= b.hIndex && a.vIndex <= b.vIndex ? -1 : 1));
        return sortedTargetCells[0];
    })();
    const delCells = targetCells.filter((cell) => cell.__renderId !== targetCell.__renderId);
    // 最右下角的cell
    const lastCell = (() => {
        const sortedTargetCells = targetCells.sort((a, b) =>
            a.hIndex + a.colSpan <= b.hIndex + b.colSpan && a.vIndex + a.rowSpan <= b.vIndex + b.rowSpan ? -1 : 1,
        );
        return sortedTargetCells[sortedTargetCells.length - 1];
    })();

    // 1. 文字合并
    const newContent =
        targetCell.content + delCells.reduce((res, cell) => res + (cell.content.length ? '\n' + cell.content : ''), '');
    targetCell.content = newContent;

    // 2. targetCell尺寸修改
    targetCell.colSpan = lastCell.hIndex + lastCell.colSpan - targetCell.hIndex;
    targetCell.rowSpan = lastCell.vIndex + lastCell.rowSpan - targetCell.vIndex;

    // 3. 删除其它cells
    delCells.forEach((c) => {
        newTableLayer.data.cells[c.vIndex][c.hIndex] = null;
    });

    // 4. 更新layout
    applyTableLayout(newTableLayer, { mode: 'repair' });

    newTableLayer = addOrUpdateLayer({
        frameworkIdentity,
        layer: newTableLayer,
        resetMaterialIds: true,
    });
    framework.service.frame.setActiveLayer(newTableLayer.id);
    framework.service.history.write(cloneDeep(tableLayer), newTableLayer);

    const newCellsSelectedInfo: CellsSelectedInfo = {
        rect: {
            startHIndex: targetCell.hIndex,
            startVIndex: targetCell.vIndex,
            endHIndex: targetCell.hIndex + targetCell.colSpan,
            endVIndex: targetCell.vIndex + targetCell.rowSpan,
        },
        cellInfoList: [
            {
                id: targetCell.__renderId,
                vIndex: targetCell.vIndex,
                hIndex: targetCell.hIndex,
            },
        ],
    };
    setState({
        cellsSelectedInfo: newCellsSelectedInfo,
        rowOrColSelectedRectInfo: null,
    });
};

/** 拆分单元格 */
export const cancelMergeCells = ({
    frameworkIdentity,
    tableLayer,
    targetCellId,
}: {
    frameworkIdentity: string;
    tableLayer: ITableLayer;
    targetCellId: string;
}) => {
    const framework = getFramework(frameworkIdentity);
    const { setState } = getTableEditState(frameworkIdentity);
    const originalTargetCell = tableLayer.data.cells.flat().find((c) => c?.__renderId === targetCellId);
    if (!originalTargetCell) return;
    const isMergedCell = originalTargetCell.colSpan > 1 || originalTargetCell.rowSpan > 1;
    if (!isMergedCell) return;

    let newTableLayer = cloneDeep(tableLayer);
    const targetCell = newTableLayer.data.cells.flat().find((c) => c?.__renderId === targetCellId)!;

    const newSelectedCellInfoList: { id: string; hIndex: number; vIndex: number }[] = [
        {
            id: targetCell.__renderId,
            hIndex: targetCell.hIndex,
            vIndex: targetCell.vIndex,
        },
    ];
    // 1. targetCell变为最左上角的cell
    targetCell.colSpan = 1;
    targetCell.rowSpan = 1;

    // 2. 补充其它cell
    for (
        let hIndex = originalTargetCell.hIndex;
        hIndex < originalTargetCell.hIndex + originalTargetCell.colSpan;
        hIndex++
    ) {
        for (
            let vIndex = originalTargetCell.vIndex;
            vIndex < originalTargetCell.vIndex + originalTargetCell.rowSpan;
            vIndex++
        ) {
            if (hIndex === originalTargetCell.hIndex && vIndex === originalTargetCell.vIndex) continue;

            const newCellId = uuidv4();

            const newCell = cloneDeep(originalTargetCell);
            newCell.__renderId = newCellId;
            newCell.hIndex = hIndex;
            newCell.vIndex = vIndex;
            newCell.colSpan = 1;
            newCell.rowSpan = 1;
            newCell.content = '';
            newTableLayer.data.cells[vIndex][hIndex] = newCell;

            newSelectedCellInfoList.push({
                id: newCell.__renderId,
                hIndex: newCell.hIndex,
                vIndex: newCell.vIndex,
            });
        }
    }

    applyTableLayout(newTableLayer, { mode: 'repair' });

    newTableLayer = addOrUpdateLayer({
        frameworkIdentity,
        layer: newTableLayer,
        resetMaterialIds: false,
    });
    framework.service.frame.setActiveLayer(newTableLayer.id);
    framework.service.history.write(cloneDeep(tableLayer), newTableLayer);

    const newCellsSelectedInfo: CellsSelectedInfo = {
        cellInfoList: newSelectedCellInfoList,
        rect: {
            startHIndex: targetCell.hIndex,
            startVIndex: targetCell.vIndex,
            endHIndex: originalTargetCell.hIndex + originalTargetCell.colSpan,
            endVIndex: originalTargetCell.vIndex + originalTargetCell.rowSpan,
        },
    };
    setState({
        cellsSelectedInfo: newCellsSelectedInfo,
        rowOrColSelectedRectInfo: null,
    });
};
